import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";

import { PageHero, Section } from "@/components/site/section";
import { FixtureCard, NewsCard, StatTile } from "@/components/site/cards";
import { fixturesQuery, leagueTableQuery, newsByCategoryQuery } from "@/lib/site-queries";

export const Route = createFileRoute("/primera-division")({
  head: () => ({
    meta: [
      { title: "Argentina Primera Divisiòn | Argentinian.Soccer" },
      { name: "description", content: "Argentina Primera División: news, league table, fixtures and statistics." },
      { property: "og:title", content: "Argentina Primera Divisiòn | Argentinian.Soccer" },
      { property: "og:url", content: "/primera-division" },
    ],
    links: [{ rel: "canonical", href: "/primera-division" }],
  }),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(newsByCategoryQuery("primera", 9)),
      context.queryClient.ensureQueryData(leagueTableQuery()),
      context.queryClient.ensureQueryData(fixturesQuery("senior", 4)),
    ]),
  component: PrimeraPage,
});

function PrimeraPage() {
  const { data: news } = useSuspenseQuery(newsByCategoryQuery("primera", 9));
  const { data: table } = useSuspenseQuery(leagueTableQuery());
  const { data: fixtures } = useSuspenseQuery(fixturesQuery("senior", 4));

  return (
    <>
      <PageHero
        eyebrow="Primera División"
        title="Argentina Primera Divisiòn"
        description="The drama, the rivalries, the players who make Argentine club football the loudest league on earth."
      />

      <Section id="news" eyebrow="Primera" title="News">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {news.map((a) => <NewsCard key={a.id} article={a} />)}
        </div>
      </Section>

      <Section id="league-table" eyebrow="Standings" title="League Table">
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left">
              <tr>
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Club</th>
                <th className="px-4 py-3 text-center">P</th>
                <th className="px-4 py-3 text-center">W</th>
                <th className="px-4 py-3 text-center">D</th>
                <th className="px-4 py-3 text-center">L</th>
                <th className="px-4 py-3 text-center">GF</th>
                <th className="px-4 py-3 text-center">GA</th>
                <th className="px-4 py-3 text-center font-bold">Pts</th>
              </tr>
            </thead>
            <tbody>
              {table.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-4 py-3 font-bold text-primary">{r.position}</td>
                  <td className="px-4 py-3 font-medium">{r.team}</td>
                  <td className="px-4 py-3 text-center">{r.played}</td>
                  <td className="px-4 py-3 text-center">{r.won}</td>
                  <td className="px-4 py-3 text-center">{r.drawn}</td>
                  <td className="px-4 py-3 text-center">{r.lost}</td>
                  <td className="px-4 py-3 text-center">{r.goals_for}</td>
                  <td className="px-4 py-3 text-center">{r.goals_against}</td>
                  <td className="px-4 py-3 text-center font-bold">{r.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="fixtures" eyebrow="Primera" title="Fixtures">
        <div className="grid gap-5 md:grid-cols-2">
          {fixtures.map((f) => <FixtureCard key={f.id} fixture={f} />)}
        </div>
      </Section>

      <Section id="statistics" eyebrow="Primera" title="Statistics">
        <div className="grid gap-4 md:grid-cols-3">
          <StatTile label="Teams" value={table.length || 28} />
          <StatTile label="Top scorer" value={table[0]?.team ?? "—"} sub="Most goals scored" />
          <StatTile label="Best defense" value={
            [...table].sort((a, b) => (a.goals_against ?? 0) - (b.goals_against ?? 0))[0]?.team ?? "—"
          } sub="Fewest goals conceded" />
        </div>
      </Section>
    </>
  );
}
